import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dailymedAPI } from '../services/api';
import SEO from '../components/SEO';

function DailyMed() {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recentSearches, setRecentSearches] = useState([]);

  // Load recent searches from localStorage on component mount
  useEffect(() => {
    const saved = localStorage.getItem('dailymed_recent_searches');
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  }, []);

  // Save search to recent searches
  const addToRecentSearches = (query) => {
    const updated = [query, ...recentSearches.filter(q => q !== query)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('dailymed_recent_searches', JSON.stringify(updated));
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await dailymedAPI.search(searchQuery, 10, 1);
      setResults(response.data);
      addToRecentSearches(searchQuery);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderResults = () => {
    if (!results) return null;

    if (results.error) {
      return (
        <div style={{ marginTop: '2rem' }}>
          <p style={{ color: '#e74c3c' }}>⚠️ {results.error}</p>
        </div>
      );
    }

    if (!results.labels || results.labels.length === 0) {
      return <p className="no-results">No drug labels found for "{searchQuery}"</p>;
    }

    return (
      <div className="results-grid">
        {results.labels.map((label, index) => (
          <Link 
            to={`/dailymed/${label.setid}`} 
            key={index} 
            className="clickable-card" 
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <div className="result-card hoverable-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                <h3 style={{ margin: 0 }}>{label.title || 'Drug Label'}</h3>
                <span style={{ 
                  backgroundColor: '#16a085', 
                  color: 'white', 
                  padding: '0.25rem 0.5rem', 
                  borderRadius: '4px', 
                  fontSize: '0.75rem',
                  fontWeight: 'bold'
                }}>DailyMed</span>
              </div>
            
            <div style={{ marginTop: '1rem' }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Label Information</h4>
              <p><strong>Set ID:</strong> <span style={{ color: '#3498db', fontSize: '0.85em', fontFamily: 'monospace' }}>{label.setid}</span></p>
              {label.published_date && (
                <p><strong>Published:</strong> {new Date(label.published_date).toLocaleDateString()}</p>
              )}
              {label.version_number && (
                <p><strong>Version:</strong> {label.version_number}</p>
              )}
            </div>

            {label.labeler && (
              <div style={{ marginTop: '1rem' }}>
                <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Manufacturer</h4>
                <p>{label.labeler}</p>
              </div>
            )}

            {(label.product_type || label.marketing_category || label.application_number) && (
              <div style={{ marginTop: '1rem' }}>
                <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Regulatory Information</h4>
                {label.product_type && <p><strong>Product Type:</strong> {label.product_type}</p>}
                {label.marketing_category && <p><strong>Marketing Category:</strong> {label.marketing_category}</p>}
                {label.application_number && <p><strong>Application Number:</strong> {label.application_number}</p>}
              </div>
            )}

            {label.active_ingredients && label.active_ingredients.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Active Ingredients</h4>
                <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
                  {label.active_ingredients.map((ingredient, i) => (
                    <li key={i}>{ingredient.name} {ingredient.strength && `- ${ingredient.strength}`}</li>
                  ))}
                </ul>
              </div>
            )}

            {label.route && label.route.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Routes of Administration</h4>
                <p>{Array.isArray(label.route) ? label.route.join(', ') : label.route}</p>
              </div>
            )}

            {label.ndc && label.ndc.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>NDC Codes</h4>
                <p style={{ fontSize: '0.85em', fontFamily: 'monospace' }}>
                  {label.ndc.slice(0, 3).join(', ')}
                  {label.ndc.length > 3 && ` (+${label.ndc.length - 3} more)`}
                </p>
              </div>
            )}

            {label.author && (
              <div style={{ marginTop: '1rem' }}>
                <p style={{ fontSize: '0.85em', color: '#7f8c8d' }}>
                  <strong>Author:</strong> {label.author}
                </p>
              </div>
            )}
            
            <div className="view-details-link">
              View Full Details →
            </div>
          </div>
          </Link>
        ))}
      </div>
    );
  };

  // SEO structured data
  const seoStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    'name': 'DailyMed Drug Labels',
    'description': 'Search DailyMed for official FDA drug label information and package inserts',
    'specialty': 'Pharmaceutical Sciences'
  };

  return (
    <div className="page-container">
      <SEO
        title="DailyMed Drug Labels - Official FDA Package Inserts & Prescribing Information"
        description="Search DailyMed, the official provider of FDA label information (package inserts). Access comprehensive drug labeling submitted to the FDA and official prescribing information."
        keywords="DailyMed, drug labels, package inserts, FDA labels, prescribing information, drug labeling, medication information, SPL"
        canonical="/dailymed"
        structuredData={seoStructuredData}
      />
      <h1 className="page-title">DailyMed Drug Labels</h1>
      <p className="page-description">
        Search DailyMed, the official provider of FDA label information (package inserts). 
        Access comprehensive drug labeling submitted to the FDA and official prescribing information.
      </p>

      {recentSearches.length > 0 && (
        <div style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#f0f4f8', borderRadius: '8px' }}>
          <h3 style={{ color: '#2c3e50', marginBottom: '0.8rem' }}>Recent Searches</h3>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {recentSearches.map((search, index) => (
              <button
                key={index}
                onClick={() => {
                  setSearchQuery(search);
                  // Simulate form submission
                  const event = new Event('submit', { bubbles: true });
                  event.preventDefault = () => {};
                  handleSearch(event);
                }}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#3498db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#2980b9'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#3498db'}
              >
                {search}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="search-section">
        <form onSubmit={handleSearch}>
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Enter drug name (e.g., aspirin, insulin, atorvastatin)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button type="submit" className="search-button" disabled={loading}>
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </form>
      </div>

      {error && <div className="error">Error: {error}</div>}
      
      {loading && <div className="loading">Loading...</div>}

      {!loading && results && (
        <div className="results-section">
          <h2>Results ({results.count || 0})</h2>
          {renderResults()}
          {results.metadata && results.metadata.total_pages > 1 && (
            <p style={{ marginTop: '1rem', color: '#7f8c8d' }}>
              Showing page 1 of {results.metadata.total_pages}
            </p>
          )}
        </div>
      )}

      <div style={{ marginTop: '3rem', padding: '1.5rem', backgroundColor: '#ecf0f1', borderRadius: '8px' }}>
        <h3 style={{ color: '#2c3e50', marginBottom: '1rem' }}>About DailyMed</h3>
        <p style={{ color: '#7f8c8d', lineHeight: '1.6' }}>
          DailyMed is the official provider of FDA label information (package inserts) from submitters of 
          applications to the FDA. Drug labeling in DailyMed includes FDA-approved labeling as well as 
          labeling for products that may not require FDA approval. DailyMed provides trustworthy information 
          about marketed drugs in the United States.
        </p>
      </div>
    </div>
  );
}

export default DailyMed;
